/**
 * Друкована форма декларації з ПДВ + усіх ЗАПОВНЕНИХ додатків (2026-08-05,
 * рішення власника: кнопка «друк» — бухгалтер роздруковує весь пакет).
 *
 * Той самий бланк, що у редакторі (VatDeclarationBlank/VatAppendixTab у
 * print-режимі: значення текстом, без сірих/жовтих фонів), кожна форма —
 * з нової сторінки. Кнопка «друкувати» → діалог браузера (@media print
 * ховає тулбар).
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  vatDeclarations,
  type VatAppendixCode,
} from '@/api/vatDeclarations';
import { orgs } from '@/api/catalogs';
import VatAppendixTab from './VatAppendixTab';
import {
  APP_LABEL,
  DeclarationHeader,
  DeclarationSections,
  MONTHS_UK,
  SignaturesBlock,
} from './VatDeclarationBlank';
import {
  CorrectionFineBlock,
  CorrectionHeader,
  CorrectionSections,
  CorrectionT2,
} from './VatCorrectionBlank';

const APPENDIX_ORDER: VatAppendixCode[] = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6'];

export default function VatDeclarationDocPrintPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const docQ = useQuery({
    queryKey: ['vat-declaration', id],
    queryFn: () => vatDeclarations.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const specsQ = useQuery({
    queryKey: ['vat-appendix-specs'],
    queryFn: vatDeclarations.appendixSpecs,
    staleTime: 3_600_000,
  });
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);

  if (!d || !specsQ.data) return <div className="card">Завантаження…</div>;

  const appHasData = (code: VatAppendixCode): boolean => {
    const a = (d.appendix_data ?? {})[code];
    if (!a) return false;
    if (Object.values(a.fields ?? {}).some((v) => v !== null && v !== '' && v !== 0)) return true;
    return Object.entries(a).some(([k, v]) =>
      k !== 'fields' && Array.isArray(v) && v.length > 0);
  };
  const includedApps = APPENDIX_ORDER.filter(
    (c) => (d.appendices ?? []).includes(c) && appHasData(c));

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 portrait; margin: 1.2cm; }
          .print-page { page-break-before: always; }
          .print-page:first-of-type { page-break-before: auto; }
          /* Ховаємо каркас застосунку (sidebar/topbar), лишаємо лише бланк. */
          nav, aside, header { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="no-print" style={{ display: 'flex', gap: '0.5rem',
        padding: '0.6rem 1rem', borderBottom: '1px solid #e2e8f0',
        alignItems: 'center' }}>
        <b>
          {d.report_type === 'CORRECTING'
            ? `Друк: уточнюючий розрахунок з ПДВ за ${d.corrected_period_month
                ? MONTHS_UK[d.corrected_period_month - 1] : ''} ${d.corrected_period_year ?? ''}`
            : `Друк: декларація з ПДВ — ${MONTHS_UK[d.period_month - 1]} ${d.period_year}`}
        </b>
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
          {d.report_type === 'CORRECTING' ? ''
            : includedApps.length
              ? `+ додатки: ${includedApps.map((c) => APP_LABEL[c]).join(', ')}`
              : 'без додатків'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="primary" onClick={() => window.print()}>друкувати</button>
          <button onClick={() => navigate(`/taxation/vat-declarations/${id}`)}>
            до декларації
          </button>
        </div>
      </div>

      <div style={{ maxWidth: d.report_type === 'CORRECTING' ? 1100 : 900,
        margin: '0 auto', padding: '1rem' }}>
        {/* Уточнюючий розрахунок — окремий бланк (було/стало/різниця). */}
        {d.report_type === 'CORRECTING' && (
          <div className="print-page">
            <CorrectionHeader doc={d} org={org} print />
            <CorrectionSections values={d.values ?? {}} readOnly print />
            <CorrectionFineBlock values={d.values ?? {}} readOnly print />
            <CorrectionT2
              rows={(((d.appendix_data ?? {})['UR_T2'] as any)?.rows as any[]) ?? []}
              readOnly print />
            <SignaturesBlock doc={d} org={org} print />
          </div>
        )}

        {/* Основна декларація */}
        {d.report_type !== 'CORRECTING' && (
        <div className="print-page">
          <DeclarationHeader doc={d} org={org} includedApps={includedApps} print />
          <DeclarationSections values={d.values ?? {}} readOnly print />
          <SignaturesBlock doc={d} org={org} print />
        </div>
        )}

        {/* Додатки з даними — кожен з нової сторінки */}
        {includedApps.filter((c) => specsQ.data?.[c]).map((c) => (
          <div key={c} className="print-page" style={{ marginTop: '1.5rem' }}>
            <VatAppendixTab
              spec={specsQ.data![c]!}
              appLabel={APP_LABEL[c]}
              data={(d.appendix_data ?? {})[c] ?? { fields: {} }}
              doc={d}
              org={org}
              readOnly
              print
            />
          </div>
        ))}
      </div>
    </div>
  );
}
